import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Grid, Typography, Skeleton, Box, Button } from "@mui/material";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Components
import { PageContainer, PageContent } from "@ui/components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { FSelect } from "@ui/components/inputs/FSelect";
import { FDatePicker } from "@ui/components/inputs/FDatePicker";
import { FTimePicker } from "@ui/components/inputs/FTimePicker";
import { TemperatureChart } from "./TemperatureChart";
import { TemperatureLog } from "./TemperatureLog";
import { EventLog } from "./EventLog";
// Redux
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getNodeTemperatures, getNodesDropdown, getNodeReplacementHistoryOptions, } from "@reducers/nodes/thunk/nodes-thunk";
import { clearNodeLogState } from "@reducers/nodes/slice/node-log-slice";
const DEFAULT_PERIOD = "5d";
const addPeriodToDate = (from, period) => {
    const end = new Date(from);
    switch (period) {
        case "31d":
            end.setDate(end.getDate() + 31);
            break;
        case "14d":
            end.setDate(end.getDate() + 14);
            break;
        case "7d":
            end.setDate(end.getDate() + 7);
            break;
        case "5d":
            end.setDate(end.getDate() + 5);
            break;
        case "2d":
            end.setDate(end.getDate() + 2);
            break;
        case "24h":
            end.setHours(end.getHours() + 24);
            break;
        case "12h":
            end.setHours(end.getHours() + 12);
            break;
        case "6h":
            end.setHours(end.getHours() + 6);
            break;
        case "3h":
            end.setHours(end.getHours() + 3);
            break;
        case "2h":
            end.setHours(end.getHours() + 2);
            break;
        case "1h":
            end.setHours(end.getHours() + 1);
            break;
        case "30min":
            end.setMinutes(end.getMinutes() + 30);
            break;
        default: end.setDate(end.getDate() + 5);
    }
    return end;
};
const getNodeLogPeriodOptions = (t) => [
    { value: "31d", label: `31 ${t("common:labels.days")}` },
    { value: "14d", label: `14 ${t("common:labels.days")}` },
    { value: "7d", label: `7 ${t("common:labels.days")}` },
    { value: "5d", label: `5 ${t("common:labels.days")}` },
    { value: "2d", label: `2 ${t("common:labels.days")}` },
    { value: "24h", label: `24 ${t("common:labels.hours")}` },
    { value: "12h", label: `12 ${t("common:labels.hours")}` },
    { value: "6h", label: `6 ${t("common:labels.hours")}` },
    { value: "3h", label: `3 ${t("common:labels.hours")}` },
    { value: "2h", label: `2 ${t("common:labels.hours")}` },
    { value: "1h", label: `1 ${t("common:labels.hour")}` },
    { value: "30min", label: `30 ${t("common:labels.minutes")}` },
];
export const NodeLog = () => {
    var _a, _b, _c, _d;
    const { t } = useTranslation(["form", "validation", "common"]);
    const { nodeId, apId } = useParams();
    const dispatch = useAppDispatch();
    const { nodeTemperatures, temperaturesLoading, nodesDropdown, dropdownLoading, replacementHistoryOptions, replacementHistoryLoading, } = useAppSelector((state) => state.nodeLog);
    const [selectedNodeUid, setSelectedNodeUid] = useState(nodeId || "");
    const deviceInitializedRef = useRef(false);
    const defaultStartDate = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);
    const [appliedFilters, setAppliedFilters] = useState(() => {
        const from = new Date();
        from.setHours(0, 0, 0, 0);
        return { fromDate: from, toDate: addPeriodToDate(from, DEFAULT_PERIOD) };
    });
    const [tempLogPagination, setTempLogPagination] = useState({ page: 0, pageSize: 25 });
    const [eventLogPagination, setEventLogPagination] = useState({ page: 0, pageSize: 25 });
    const [loadingTable, setLoadingTable] = useState(null);
    const { control, setValue, getValues, watch } = useForm({
        defaultValues: {
            device: nodeId || "",
            startDate: defaultStartDate,
            startTime: null,
            period: DEFAULT_PERIOD,
        },
    });
    const selectedDevice = watch("device");
    const periodOptions = getNodeLogPeriodOptions(t);
    const replacementHistoryCurrent = useMemo(() => replacementHistoryOptions.find((o) => o.value === "current"), [replacementHistoryOptions]);
    const replacementHistoryPrevious = useMemo(() => replacementHistoryOptions.find((o) => o.value !== "current"), [replacementHistoryOptions]);
    useEffect(() => {
        if (apId) {
            dispatch(getNodesDropdown(apId));
        }
        return () => {
            dispatch(clearNodeLogState());
        };
    }, [dispatch, apId]);
    useEffect(() => {
        if (!apId || !selectedDevice)
            return;
        dispatch(getNodeReplacementHistoryOptions({
            nodeUid: selectedDevice,
            accessPointUid: apId,
        }));
    }, [apId, selectedDevice, dispatch]);
    useEffect(() => {
        if (selectedDevice) {
            dispatch(getNodeTemperatures({
                nodeUid: selectedDevice,
                fromDate: appliedFilters.fromDate.toISOString(),
                toDate: appliedFilters.toDate.toISOString(),
                temperatureLogPageNumber: tempLogPagination.page + 1,
                temperatureLogPageSize: tempLogPagination.pageSize,
                eventLogPageNumber: eventLogPagination.page + 1,
                eventLogPageSize: eventLogPagination.pageSize,
            }));
        }
    }, [
        dispatch,
        selectedDevice,
        appliedFilters,
        tempLogPagination.page,
        tempLogPagination.pageSize,
        eventLogPagination.page,
        eventLogPagination.pageSize,
    ]);
    useEffect(() => {
        if (!temperaturesLoading) {
            setLoadingTable(null);
        }
    }, [temperaturesLoading]);
    useEffect(() => {
        if (!(nodesDropdown === null || nodesDropdown === void 0 ? void 0 : nodesDropdown.length) || deviceInitializedRef.current)
            return;
        deviceInitializedRef.current = true;
        if (nodeId) {
            const foundNode = nodesDropdown.find((n) => n.nodeUid === nodeId);
            const initialDevice = foundNode ? foundNode.nodeUid : nodesDropdown[0].nodeUid;
            setValue("device", initialDevice);
            setSelectedNodeUid(initialDevice);
        }
        else {
            setValue("device", nodesDropdown[0].nodeUid);
            setSelectedNodeUid(nodesDropdown[0].nodeUid);
        }
    }, [nodesDropdown, nodeId, setValue]);
    useEffect(() => {
        if (selectedDevice && selectedDevice !== selectedNodeUid) {
            setSelectedNodeUid(selectedDevice);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            setValue("startDate", today);
            setValue("startTime", null);
            setValue("period", DEFAULT_PERIOD);
            setAppliedFilters({ fromDate: today, toDate: addPeriodToDate(today, DEFAULT_PERIOD) });
            setTempLogPagination((p) => (Object.assign(Object.assign({}, p), { page: 0 })));
            setEventLogPagination((p) => (Object.assign(Object.assign({}, p), { page: 0 })));
        }
    }, [selectedDevice, selectedNodeUid, setValue]);
    const handleSearch = useCallback(() => {
        const { startDate, startTime, period } = getValues();
        const from = startDate ? new Date(startDate) : new Date(defaultStartDate);
        from.setSeconds(0, 0);
        if (startTime) {
            const st = new Date(startTime);
            from.setHours(st.getHours(), st.getMinutes(), 0, 0);
        }
        else {
            from.setHours(0, 0, 0, 0);
        }
        const to = addPeriodToDate(from, period || DEFAULT_PERIOD);
        setTempLogPagination((p) => (Object.assign(Object.assign({}, p), { page: 0 })));
        setEventLogPagination((p) => (Object.assign(Object.assign({}, p), { page: 0 })));
        setAppliedFilters({ fromDate: from, toDate: to });
    }, [getValues, defaultStartDate]);
    const deviceOptions = useMemo(() => {
        if (nodesDropdown === null || nodesDropdown === void 0 ? void 0 : nodesDropdown.length) {
            return nodesDropdown.map((node) => ({
                label: node.nodeName || node.nodeUid,
                value: node.nodeUid,
            }));
        }
        if (nodeId) {
            return [{ label: nodeId, value: nodeId }];
        }
        return [];
    }, [nodesDropdown, nodeId]);
    const selectedNodeName = useMemo(() => {
        if (!selectedNodeUid)
            return "Node";
        const node = nodesDropdown === null || nodesDropdown === void 0 ? void 0 : nodesDropdown.find((n) => n.nodeUid === selectedNodeUid);
        return (node === null || node === void 0 ? void 0 : node.nodeName) || (node === null || node === void 0 ? void 0 : node.nodeUid) || selectedNodeUid;
    }, [nodesDropdown, selectedNodeUid]);
    const chartLogs = (_b = (_a = nodeTemperatures === null || nodeTemperatures === void 0 ? void 0 : nodeTemperatures.temperatureLogsForChart) !== null && _a !== void 0 ? _a : nodeTemperatures === null || nodeTemperatures === void 0 ? void 0 : nodeTemperatures.temperatureLogs) !== null && _b !== void 0 ? _b : [];
    const temperatureLogData = useMemo(() => {
        if (!(nodeTemperatures === null || nodeTemperatures === void 0 ? void 0 : nodeTemperatures.temperatureLogs))
            return [];
        return nodeTemperatures.temperatureLogs.map((log, index) => {
            var _a;
            return ({
                id: index + 1,
                dateTime: log.dateTime,
                temperature: log.temperature !== null ? `${log.temperature} °C` : "N/A",
                fatDuration: (_a = log.fatDuration) !== null && _a !== void 0 ? _a : null,
            });
        });
    }, [nodeTemperatures]);
    const eventLogData = useMemo(() => {
        if (!(nodeTemperatures === null || nodeTemperatures === void 0 ? void 0 : nodeTemperatures.eventLogs))
            return [];
        return nodeTemperatures.eventLogs.map((log, index) => ({
            id: index + 1,
            dateTime: log.dateTime,
            statusType: log.statusType || "0",
            flag: log.flag || "None",
            battery: log.battery,
            rssi: log.rssi,
            version: log.version,
            debug: log.debug,
            temperature: log.temperature !== null ? `${log.temperature} °C` : "N/A",
        }));
    }, [nodeTemperatures]);
    const isLoading = temperaturesLoading || dropdownLoading;
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t(""), showBackButton: true }),
        React.createElement(PageContent, null,
            React.createElement(Grid, { container: true, spacing: 3 },
                React.createElement(Grid, { size: { xs: 12 } }, isLoading ? (React.createElement(Skeleton, { variant: "text", width: 200, height: 40 })) : (React.createElement(Typography, { variant: "h4", color: "primary.darker" },
                    selectedNodeName,
                    " Logging"))),
                React.createElement(Grid, { size: { xs: 12 } }, replacementHistoryLoading ? (React.createElement(Skeleton, { variant: "rounded", height: 48, sx: { mb: 1 } })) : ((replacementHistoryCurrent || replacementHistoryPrevious) && (React.createElement(Box, { sx: {
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        border: 1,
                        borderColor: "divider",
                    } },
                    replacementHistoryCurrent && (React.createElement(Typography, { variant: "body2", color: "text.secondary" }, replacementHistoryCurrent.label)),
                    replacementHistoryPrevious && (React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 0.5 } }, replacementHistoryPrevious.label)))))),
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(Grid, { container: true, spacing: 1 },
                        React.createElement(Grid, { size: { xs: 12, md: 4 } }, apId && dropdownLoading ? (React.createElement(Skeleton, { variant: "rounded", height: 56 })) : (React.createElement(FSelect, { name: "device", control: control, label: t("common:placeholders.chooseADevice"), options: deviceOptions, disabled: !apId && deviceOptions.length <= 1 }))),
                        React.createElement(Grid, { size: { xs: 12, sm: 6, md: 2 } },
                            React.createElement(FDatePicker, { name: "startDate", control: control, label: t("common:labels.from"), disableFuture: true })),
                        React.createElement(Grid, { size: { xs: 12, sm: 6, md: 2 } },
                            React.createElement(FTimePicker, { name: "startTime", control: control, label: t("common:labels.startTimeOptional") })),
                        React.createElement(Grid, { size: { xs: 12, sm: 6, md: 2 } },
                            React.createElement(FSelect, { name: "period", control: control, label: t("common:labels.period"), options: periodOptions, disableClearable: true })),
                        React.createElement(Grid, { size: { xs: 12, sm: 6, md: 2 } },
                            React.createElement(Box, { sx: { display: "flex", alignItems: "flex-end", height: "100%" } },
                                React.createElement(Button, { variant: "contained", onClick: handleSearch, disabled: temperaturesLoading, fullWidth: true, sx: { height: 56 } }, t("common:actions.search")))))),
                React.createElement(Grid, { size: { xs: 12 } }, temperaturesLoading && !nodeTemperatures ? (React.createElement(Box, { sx: { p: 2, bgcolor: "background.paper", borderRadius: 2 } },
                    React.createElement(Skeleton, { variant: "text", width: 150, height: 30, sx: { mb: 2 } }),
                    React.createElement(Skeleton, { variant: "rectangular", height: 300 }))) : (React.createElement(TemperatureChart, { logs: chartLogs, title: selectedNodeName }))),
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(Grid, { container: true, spacing: 3 },
                        React.createElement(Grid, { size: { xs: 12, lg: 6 } },
                            React.createElement(TemperatureLog, { data: temperatureLogData, loading: temperaturesLoading &&
                                    (loadingTable === null || loadingTable === "temp"), rowCount: (_c = nodeTemperatures === null || nodeTemperatures === void 0 ? void 0 : nodeTemperatures.totalTemperatureLogCount) !== null && _c !== void 0 ? _c : 0, paginationModel: tempLogPagination, onPaginationModelChange: (model) => {
                                    setLoadingTable("temp");
                                    setTempLogPagination(model);
                                }, pageSizeOptions: [25, 50] })),
                        React.createElement(Grid, { size: { xs: 12, lg: 6 } },
                            React.createElement(EventLog, { data: eventLogData, loading: temperaturesLoading &&
                                    (loadingTable === null || loadingTable === "event"), rowCount: (_d = nodeTemperatures === null || nodeTemperatures === void 0 ? void 0 : nodeTemperatures.totalEventLogCount) !== null && _d !== void 0 ? _d : 0, paginationModel: eventLogPagination, onPaginationModelChange: (model) => {
                                    setLoadingTable("event");
                                    setEventLogPagination(model);
                                }, pageSizeOptions: [25, 50] }))))))));
};
export default NodeLog;
